<template>
    <div>
        <h1 class="service-title title is-1">Tree Relocation</h1>
        <affix>
            <template slot="navigation" scope="props">
                <div :class="{'service-navigation': true, 'has-affix': affixed}" ref="subnav">
                    <nav :class="{'subnav-affix': affixed}">
                        <ul>
                            <li :class="{'is-active has-active': props.activeTrigger === 'why-a-tree-spade' }">
                                <a href="#why-a-tree-spade">Why a Tree Spade</a>
                            </li>
                            <li :class="{
                                'is-active has-active': props.activeTrigger === 'the-process',
                                'has-active': ['digging-the-hole', 'digging-the-tree', 'planting-the-tree'].indexOf(props.activeTrigger) !== -1,
                            }">
                                <a href="#the-process">The Process</a>
                                <ul class="subnav">
                                    <li :class="{'is-active': props.activeTrigger === 'digging-the-hole' }">
                                        <a href="#digging-the-hole">Digging the Hole</a>
                                    </li>
                                    <li :class="{'is-active': props.activeTrigger === 'digging-the-tree' }">
                                        <a href="#digging-the-tree">Digging the Tree</a>
                                    </li>
                                    <li :class="{'is-active': props.activeTrigger === 'planting-the-tree' }">
                                        <a href="#planting-the-tree">Planting the Tree</a>
                                    </li>
                                </ul>
                            </li>
                            <li :class="{
                                'is-active has-active': props.activeTrigger === 'site-considerations',
                                'has-active': ['design', 'soil-type', 'planting-depth'].indexOf(props.activeTrigger) !== -1,
                            }">
                                <a href="#site-considerations">Site Considerations</a>
                                <ul class="subnav">
                                    <li :class="{'is-active': props.activeTrigger === 'design' }">
                                        <a href="#design">Design</a>
                                    </li>
                                    <li :class="{'is-active': props.activeTrigger === 'soil-type' }">
                                        <a href="#soil-type">Soil Type</a>
                                    </li>
                                    <li :class="{'is-active': props.activeTrigger === 'planting-depth' }">
                                        <a href="#planting-depth">Planting Depth</a>
                                    </li>
                                </ul>
                            </li>
                            <li :class="{'is-active has-active': props.activeTrigger === 'post-relocation-care'}">
                                <a href="#post-relocation-care">Post-Relocation Care</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </template>

            <div slot="content" class="service-content">
                <div class="page-section">
                    <a class="affix-trigger" name="why-a-tree-spade"></a>
                    <h1 class="title">Why a Tree Spade</h1>
                    <p>
                        Tree spading is the optimal method for moving and transplanting large trees from one site to another.
                        The following are some reasons for transplanting large trees vs. younger, smaller trees:
                    </p>
                    <div class="content">
                        <ul>
                            <li>To create a mature landscape quickly.</li>
                            <li>To alter the design of a landscape.</li>
                            <li>To create space for a new building addition.</li>
                            <li>
                                To prevent the loss of a tree due to building, roadway expansion or other construction.
                            </li>
                            <li>A mature tree has outgrown its present location.</li>
                            <li>To move a tree to a site better suited to its needs.</li>
                        </ul>
                    </div>

                    <p>
                        Initially, transplanting a tree with a tree spade may be more costly than purchasing container
                        stock or B&B (ball and burlap) trees. However, the tree spade may be the best option if a tree will otherwise be
                        lost or if the value of the tree outweighs the moving costs. Spading also saves on labor,
                        planting time, and years of maintenance on a juvenile tree.
                    </p>

                    <p>
                        Tree spades are used in various ways throughout the green industry. Commercial nurseries use
                        tree spades to lift large, field-grown trees out of the soil, and wrap the root ball in burlap
                        and twine (termed "ball and burlap" or B&B) for retail sale or compact storage. Landscape
                        companies and arborists use spades to plant large trees that are nursery-grown or have been
                        moved from elsewhere in the landscape. Landscape professionals use a tree spade to create an
                        "instant landscape" by digging and transplanting large trees from one location to another.
                    </p>
                </div>

                <div class="content">
                    <a class="affix-trigger" name="the-process"></a>
                    <h1 class="title">The Process</h1>
                    <p>
                        Transplanting a tree with a tree spade can be summarized into 3 major steps:
                        digging the hole, digging the tree and planting the tree.
                        Many considerations must be taken into account during this process in order to alleviate
                        stress on the tree as it transitions into its new location. With our expert knowledge,
                        your tree will retain its health and resume its growth in a short time.
                    </p>

                    <a class="affix-trigger" name="digging-the-hole"></a>
                    <h2 class="subtitle">Digging the hole</h2>
                    <div class="columns">
                        <div class="column is-8">
                            <p>
                                First, a hole must be dug to receive the new tree. The tree spade used to dig the hole
                                must be the proper size for the tree that will be dug and transplanted - this will ensure
                                that the hole can accommodate a root ball substantial enough to support the tree.
                            </p>
                            <p>
                                The operator controls the spade from the rear of the truck. Hydraulic supports provide
                                a stable foundation and prevent the spade from tipping over (a root ball from a 90"
                                tree spade can weigh up to 12,000 pounds!). The blades of the spade are inserted into the
                                soil and are closed to contain the soil ball. The soil ball is positioned for
                                transport using the spade hydraulics, then lifted onto the truck and hauled away.
                                The soil ball may be used later on to fill in the hole left over by the tree
                                being transplanted.
                            </p>
                        </div>
                        <div class="column is-4">
                            <img src="../../assets/content/treeprocess1.jpg" style="width: 100%;">
                        </div>
                    </div>


                    <a class="affix-trigger" name="digging-the-tree"></a>
                    <h2 class="subtitle">Digging the tree</h2>
                    <div class="columns">
                        <div class="column is-8">
                            <p>
                                The same tree spade used to dig the hole is now used to dig the tree. Before backing the
                                spade up to the tree, the frame and rear blades are opened to encircle the tree trunk.
                                The blades are then positioned in a equidistant fashion on all sides of the trunk,
                                while also taking into account the size of the root ball.
                            </p>
                            <p>
                                The frame and rear blades are then driven down hydraulically into the soil.
                                Each of the blades is independently moved several inches at a time until they are all
                                at their maximum depth. With the blades now closed, the tree is then lifted by its
                                root ball from the surrounding soil, and positioned onto the truck for transport.
                            </p>
                            <p>
                                At this time, the operator may need to prune the roots, secure any tree limbs or palm
                                fronds, and adjust the tree spade for safe transport. Depending on the species of the
                                tree, the time of year, and the travel distance, a tarp may be used to cover the tree
                                canopy to prevent damage to the tree during transport. For highway transport, a special
                                "dirt skirt", made by <strong>HILCO</strong>, is used to cover the spades to prevent soil loss.
                            </p>
                        </div>
                        <div class="column is-4">
                            <img src="../../assets/content/treemoving.jpg" style="width: 100%;">
                        </div>
                    </div>


                    <a class="affix-trigger" name="planting-the-tree"></a>
                    <h2 class="subtitle">Planting the tree</h2>
                    <div class="columns">
                        <div class="column is-8">
                            <p>
                                The operator now positions the spade over the previously dug hole. Adjustments may be
                                made at this time if the tree has not been growing straight. If possible, the tree
                                will be oriented in the same direction as its original location. The tree will be
                                planted roughly 2-3 inches higher than the original grade to allow for settling.
                                If a tree is planted too shallow, the roots may be damaged by temperature fluctuations
                                and a lack of soil moisture. Planting a tree too deeply will result in girdling problems
                                later on, leading to the death of the tree.
                            </p>
                            <p>
                                The tree is now lowered into the ground and the blades are removed by hydraulically
                                sliding them up and out. Once the tree spade is removed and the transplantation
                                complete, a water basin will be created at the outer edge of the soil ball. This allows
                                water to penetrate the soil ball and surrounding soil, while ensuring the root ball
                                is watered slowly and thoroughly. If large air spaces are apparent between the soil ball
                                and the surrounding soil, additional soil can be added during the initial watering to
                                ensure good soil contact for the newly developing roots. The water basin should then be
                                filled with an organic mulch, at a minimum depth of 3 inches, which will help the tree
                                and root ball retain moisture.
                            </p>
                            <p>
                                Although staking trees moved with tree spades is normally not necessary,
                                if they are used, the staking should be removed as soon as the tree is well-rooted.
                            </p>
                        </div>
                        <div class="column is-4">
                            <img src="../../assets/content/treeplanting.jpg" style="width: 100%;">
                        </div>
                    </div>
                </div>

                <div class="content">
                    <a class="affix-trigger" name="site-considerations"></a>
                    <h1 class="title">Site Considerations</h1>

                    <ul>
                        <li>
                            Individuals involved should have a general understanding of the tree spading process,
                            and be familiar with the sites where the tree spade will be operated.
                        </li>
                        <li>
                            There should be adequate access for the tree spade to the original site and the new site.
                            Because the spade is in a fixed position, it is desirable to plan the move with access to
                            the preferred side of the tree.
                            Maintaining the same exposure is beneficial to tree establishment.
                        </li>
                        <li>
                            Locate existing power lines, underground septic systems, grade changes and note any
                            restrictions they present.
                        </li>
                        <li>
                            There should be adequate space for the tree spade to operate safely. A 44-inch tree spade
                            requires approximately six feet around the tree that is unobstructed.
                            A 90-inch spade requires eight feet.
                        </li>
                        <li>
                            Current soil conditions should be relatively dry. If the soil is too wet,
                            the heavy machinery will damage the soil structure.
                            Turf should be protected with 10-12 inches of wood mulch or &frac34;” plywood.
                        </li>
                        <li>
                            When transplanting a large tree, it is important to duplicate the original conditions as
                            closely as possible, therefore reducing stress on the tree. Soil type, planting depth,
                            staking, watering and mulching are critical factors to the success of the transplant.
                        </li>
                    </ul>

                    <a class="affix-trigger" name="design"></a>
                    <h2 class="subtitle">Design</h2>
                    <p>
                        Proper placement of trees is important to the design and sustainability of the landscape.
                        It is important to select a location where the tree will not grow to interfere with structures,
                        power lines, roof lines, windows and other existing plants. The species of tree selected for a
                        site will depend on space requirements, existing plants and structures, seasonal interest
                        desired and the function the tree will serve in the landscape. Some common functions of trees
                        in landscapes are:
                    </p>

                    <ul>
                        <li>
                            Trees may be used to soften the architectural lines of a building and transition the eye
                            from the roofline to the soil.
                        </li>
                        <li>Trees may be used in a design to create shade and reduce summer cooling costs.</li>
                        <li>Trees may protect a building from winter winds and thus reduce heating costs.</li>
                        <li>Trees may act as a backdrop to a landscape.</li>
                        <li>Trees may provide screening or a ceiling over an outdoor space.</li>
                    </ul>


                    <a class="affix-trigger" name="soil-type"></a>
                    <h2 class="subtitle">Soil Type</h2>

                    <p>
                        If possible, the soil type of the new location should match the soil type, drainage and pH of
                        the original site as closely as possible. For large projects with many trees to be moved and transplanted,
                        a soil analysis is recommended to compare soil structure, porosity, and amendments required. Knowledge of
                        theses factors may favor one source of trees over another. Using a tree spade may also cause glazing, the
                        compaction of the soil in the newly-dug hole, which will affect the lateral movement of soil moisture and
                        root penetration. This can be resolved by roughing up the sides of the hole and the plug using a shovel, rake or fork.
                    </p>


                    <a class="affix-trigger" name="planting-depth"></a>
                    <h2 class="subtitle">Planting Depth</h2>

                    <p>
                        The root ball of the tree should be watered thoroughly prior to digging to keep the ball intact
                        and reduce as much soil loss as possible during transport. To allow the tree to become adequately
                        hydrated, the tree should be watered 1-2 days prior to moving the tree. When positioning the
                        tree in the new hole, it should be placed 2-3″ higher than the original grade to allow for settling.
                        If a tree is planted too shallow, the roots may be damaged by temperature fluctuations and lack
                        of soil moisture. Planting a tree too deeply will result in girdling problems later.
                        For more information:
                    </p>

                    <p>
                        The site for the transplanted tree is prepared by tilling the soil and raking it smooth.
                        Planting trees in established lawns is also a common practice. All grass within the drip line
                        of the tree should be removed and replaced with mulch. This will prevent nutrient and moisture
                        competition as well as possible mower damage.
                    </p>
                </div>

                <div class="content">
                    <a class="affix-trigger" name="post-relocation-care"></a>
                    <h1 class="title">Post-Relocation Care</h1>
                    <p>
                        Transplanted trees normally experience a two year period of transitional adjustment.
                        During that time the root system will regenerate and flourish MUCH FASTER,
                        if you closely follow our Intensified Tree Care Program.
                    </p>
                </div>

                <div class="content">
                    <h2 class="subtitle">Watering</h2>
                    <ul class="numeric-list">
                        <li>
                            Hand water the root ball area heavily, but not often based upon the soil moisture
                            content at a depth of 4"-6". Apply a hose to soak the total root ball area for several
                            hours. This is in addition to the water your trees may or may not be receiving from an
                            automated drip irrigation system. Trees require more water in late Spring, Summer and
                            Fall. They also must be kept moist through the Winter months. Trees can drown in heavy
                            clay soils that are slow to drain, therefore, always check the moisture level with in the
                            root ball area of your trees frequently by probing the soil to a depth of 12". It is best to
                            heavily water, let the root ball dry out and then heavily water again.
                        </li>
                        <li>
                            Hand water the foliage (leaves and central stem) by spraying water over the entire
                            upper crown. This should be done DAILY for one week following transplanting.
                            Thereafter, we recommend spraying the crown area once weekly to remove soot and
                            other pollutants. Trees readily absorb moisture.
                        </li>
                        <li>
                            Refilling the Temporary Irrigation Units (if supplied with your trees) Refill TWICE weekly.
                        </li>
                    </ul>
                    <p>Suggested interval: Monday and Thursday</p>
                    <table class="table">
                        <tbody>
                        <tr>
                            <td>4" - 6" caliper trees</td>
                            <td>6 gallons per day</td>
                        </tr>
                        <tr>
                            <td>7" - 10" caliper trees</td>
                            <td>16 gallons per day</td>
                        </tr>
                        </tbody>
                    </table>
                    <p>
                        Irrigation units, Tree Gators, are an extra cost accessory item,
                        which may not have been included with the planting work you contracted with us.
                    </p>
                </div>
                <div class="content">
                    <h2 class="subtitle">Mulches</h2>
                    <p>
                        Maintain a thick layer of wood chips or commercial cypress or shredded pine bark (3"-4") on
                        top of the root ball spreading from the stem to 12" beyond the root ball edge.
                        Mulch helps to conserve moisture, modify soil temperatures and reduce weed growth.
                        Avoid planting flowers shrubbery and installing tree collars within the root ball area
                        of your trees for at least two years following installation
                    </p>
                </div>
                <div class="content">
                    <h2 class="subtitle">Fertilization</h2>
                    <p>Apply a balance fertilizer with trace minerals.</p>
                    <ul class="numeric-list">
                        <li>
                            Rapid-Go or Miracle-Gro General Purpose are good water soluble fertilizer compounds.
                            Mix 2 teaspoons per gallon and apply 10 gallons of mixed solution to each
                            tree every 2 weeks for the first year
                        </li>
                        <li>
                            A second fertilizing option is to apply a balance time-release fertilizer in granular or
                            table form. Sierra Blend, Osmocote or Agriform are some of the common types.
                            Application should be applied evenly around the top of the root ball every four months.
                            Initial application of 3 Lbs. followed by 1 ½ Lbs. every six months for the first two years.
                        </li>
                    </ul>
                </div>
                <div class="content">
                    <h2 class="subtitle">Insect Control</h2>
                    <p>
                        Apply a broad spectrum granular insecticide around the top of the root ball to discourage
                        any pests such as Bark and Leaf Aphids, Spider Mites, caterpillars, beetles and ants.
                    </p>
                </div>
                <div class="content">
                    <h2 class="subtitle">Herbicides</h2>
                    <p>
                        Weed and Grass killers should not be used in the vicinity of transplanted trees and shrubs.
                        Round Up may be safely used only if the tree has 3”-4” of mulch over the root ball
                        and the spray will not contact the roots or foliage directly.
                    </p>
                </div>
            </div>
        </affix>
    </div>
</template>

<script>
    export default {
        name: 'trees',
        mounted() {
            window.addEventListener('scroll', this.handleScroll, false)
        },
        beforeDestroy() {
            window.removeEventListener('scroll', this.handleScroll, false)
        },
        data () {
            return {
                affixed: false,
            }
        },
        methods: {
            handleScroll() {
                let bounds   = this.$refs.subnav.getBoundingClientRect()
                this.affixed = bounds.top < 117
            }
        }
    }
</script>



// WEBPACK FOOTER //
// Trees.vue?0b54e0cf